import constants from "../../../../../../../../appConstants";
import {
  DropdownEditor,
  NumericEditor,
  TextFieldEditor,
  SearchEditor,
} from '@dataGrid/components/cellEditors';
import { DropdownValueCellRenderer } from '@dataGrid/components/cellRenderer';
import { MultiSelectFilter } from '@dataGrid/components/columnFilters';
import { suppressEnter, suppressKeyboardEventHandler } from '@dataGrid/gridUtilities';
import { getFieldError, isEmptyShipmentObj, fetchLocationData } from "../utils";
import { isReadOnlyPickupViewMode, isBulkEditPickupMode, isBulkVoidPickupMode } from "../../utils";
import LocationDropdownItem from "./components/LocationDropdownItem";
import { FORM_VALIDATION_MSG } from "../../constants";

export default class GridConfig {
  constructor(configuration, title, pickupManagementMode) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config, title, pickupManagementMode);
  }
  static getConfiguration(userConfig, title, pickupManagementMode) {
    return GridConfig.getDefaultConfiguration(title, userConfig, pickupManagementMode);
  }

  static setPriorityShipment(params) {
    if (params.data.priority === params.newValue?.value) {
      return false;
    }
    params.data.priority = params.newValue?.value ?? null;
    return true;
  }

  /* priority stores the mapped service level, so the cell resolves it to the display name.
   Falls back to the raw value for anything not in the option list. */
  static getPriorityShipment(params) {
    const { priority } = params.data;
    if (!priority) {
      return "None";
    }
    return (
      params.context?.serviceLevelOptions?.find(
        (option) => option.value === priority
      )?.label ?? priority
    );
  }

  static setPackageCode(params) {
    if (params.data.packageCode === params.newValue?.value) {
      return false;
    }
    params.data.packageCode = params.newValue?.value ?? null;
    return true;
  }

  static isFieldEditable(params) {
    return (
      !isReadOnlyPickupViewMode(params.api.mode) &&
      !params.api.isReadOnlyPickupMergeMode
    );
  }

  static fieldValidation(params) {
    if (!params.api.isDataUpdatedOnGrid || isEmptyShipmentObj(params.data)) {
      return false;
    }
    return Boolean(
      getFieldError({
        field: params.colDef.field,
        value: params.value,
        mode: params.api.mode,
        isZipUpdatedManually: params.data.isZipUpdatedManually
      })
    );
  }

  static setZipCode = (params) => {
    if (
      params.data.destinationZip === params.newValue?.value ||
      !params.newValue
    ) {
      return false;
    }
    params.data.destinationZip = params.newValue ?? params.newValue.zip;
    return true;
  };

  static zipDropdownValueCellRenderer = (params) => {
    return params.value && params.value.zip !== undefined
      ? params.value.zip
      : params.value;
  };

  static getPackageCode = (params) => {
    return params.data.packageCode;
  }

  static formatDefaultValueZero = (params) => {
    if(!params.value){
      return 0;
    }
    return params.value
  }

  static getDefaultZeroValue = (params) => {
    const fieldName = params.colDef.field;
    if (params.data[fieldName] === 0){
      return ""
    }
    return params.data[fieldName]
  }

  static isEmbargoShipmentErrorCell = (params) => {
    const rowIds = params.api.embargoShipmentErrorRowIds || [];
    return rowIds.includes(params.data?.pickupShipmentRowId);
  };

  static isEmbargoShipmentWarningCell = (params) => {
    if (GridConfig.hasShipmentCellError(params)) {
      return false;
    }
    const rowIds = params.api.embargoShipmentWarningRowIds || [];
    return rowIds.includes(params.data?.pickupShipmentRowId);
  };

  static hasShipmentCellError = (params) =>
    GridConfig.fieldValidation(params) || GridConfig.isEmbargoShipmentErrorCell(params);

  static getDefaultConfiguration(title, userConfig, pickupManagementMode) {
    const showPickupNumberColumn =
      isBulkEditPickupMode(pickupManagementMode) ||
      isBulkVoidPickupMode(pickupManagementMode);
    return {
      title: title,
      keyColumn: "",
      rowSelection: "multiple",
      rowDeselection: true,
      defaultColDef: {
        cellEditor: TextFieldEditor,
        lockPinned: true,
        sortable: true,
        filter: true,
        resizable: true,
        editable: GridConfig.isFieldEditable,
        lockPosition: true,
        cellDataType: false,
      },
      columns: [
        ...(showPickupNumberColumn
          ? [
              {
                headerName: "Pickup #",
                headerTooltip: "Pickup Number",
                field: "pickupNumber",
                minWidth: constants.columnWidth.PICKUP_NUMBER,
                cellClass: "gridCell-leftAlign",
                filter: MultiSelectFilter,
                editable: false,
              },
            ]
          : []),
        {
          headerName: "HU (Opt)",
          headerTooltip: "Handling Unit (Optional)",
          field: "handlingUnits",
          minWidth: constants.columnWidth.HU,
          cellClass: "gridCell-centerAlign",
          cellEditor: NumericEditor,
          filter: MultiSelectFilter,
          cellClassRules: {
            cellError: GridConfig.fieldValidation,
          },
          valueFormatter : GridConfig.formatDefaultValueZero,
          valueGetter : GridConfig.getDefaultZeroValue
        },
        {
          headerName: "Pieces (Opt)",
          field: "pieces",
          headerTooltip: "Pieces (Optional)",
          minWidth: constants.columnWidth.PIECES,
          cellClass: "gridCell-centerAlign",
          cellEditor: NumericEditor,
          filter: MultiSelectFilter,
          valueFormatter : GridConfig.formatDefaultValueZero,
          valueGetter : GridConfig.getDefaultZeroValue
        },
        {
          headerName: "Pkg Type",
          headerTooltip: "Package Type",
          field: "packageCodeWithSuffix",
          valueGetter: GridConfig.getPackageCode,
          minWidth: constants.columnWidth.PKG_TYPE,
          cellClass: "gridCell-centerAlign",
          filter: MultiSelectFilter,
          cellEditor: DropdownEditor,
          cellEditorPopup: true,
          cellRenderer: DropdownValueCellRenderer,
          suppressKeyboardEvent: suppressKeyboardEventHandler(suppressEnter),
          cellEditorParams: {
            isClearable: true,
            openMenuOnFocus: true,
          },
          cellClassRules: {
            cellError: GridConfig.fieldValidation,
          },
          valueSetter: GridConfig.setPackageCode,
        },
        {
          headerName: "Total Weight",
          field: "weight",
          minWidth: constants.columnWidth.WEIGHT,
          cellClass: "gridCell-centerAlign",
          cellEditor: NumericEditor,
          filter: MultiSelectFilter,
          cellClassRules: {
            cellError: GridConfig.fieldValidation,
          },
          valueFormatter : GridConfig.formatDefaultValueZero,
          valueGetter : GridConfig.getDefaultZeroValue
        },
        {
          headerName: "Dest Zip",
          headerTooltip: "Destination Zip",
          field: "destinationZip",
          minWidth: constants.columnWidth.ZIP_CODE,
          cellClass: "gridCell-leftAlign",
          cellEditor: SearchEditor,
          cellEditorPopup: true,
          suppressKeyboardEvent: suppressKeyboardEventHandler(suppressEnter),
          cellRenderer: GridConfig.zipDropdownValueCellRenderer,
          cellEditorParams: {
            openMenuOnFocus: true,
            placeholder: "Enter Zip",
            fetchData:  fetchLocationData,
            fieldName: "zip",
            OptionRenderer: LocationDropdownItem,
            errorMessage: FORM_VALIDATION_MSG.ZIP_CODE.INVALID_VALUE,
            terminalCode: userConfig.terminal.code
          },
          filter: MultiSelectFilter,
          cellClassRules: {
            cellError: GridConfig.hasShipmentCellError,
            cellWarning: GridConfig.isEmbargoShipmentWarningCell,
          },
          valueSetter: GridConfig.setZipCode,
        },
        {
          headerName: "Dest City",
          headerTooltip: "Destination City",
          field: "destinationCity",
          minWidth: constants.columnWidth.WEIGHT,
          cellClass: "gridCell-centerAlign",
          filter: MultiSelectFilter,
          cellClassRules: {
            cellError: GridConfig.fieldValidation,
          },
          editable: false,
        },
        {
          headerName: "Dest Term",
          headerTooltip: "Destination Terminal",
          field: "destinationTerminal",
          minWidth: constants.columnWidth.TERMINAL,
          cellClass: "gridCell-centerAlign",
          filter: MultiSelectFilter,
          cellClassRules: {
            cellError: GridConfig.fieldValidation,
          },
          editable: false,
        },
        {
          headerName: "Service Level",
          field: "serviceLevel",
          minWidth: constants.columnWidth.SERVICE_LEVEL,
          cellClass: "gridCell-centerAlign",
          filter: MultiSelectFilter,
          cellEditor: DropdownEditor,
          cellEditorPopup: true,
          cellRenderer: DropdownValueCellRenderer,
          suppressKeyboardEvent: suppressKeyboardEventHandler(suppressEnter),
          cellEditorParams: {
            openMenuOnFocus: true,
            isClearable: true,
          },
          valueSetter: GridConfig.setPriorityShipment,
          valueGetter: GridConfig.getPriorityShipment,
        },
      ],
    };
  }
}
